import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("vertdistributionsarl@gmail.com");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("vertdistributionsarl@gmail.com");
  const [forgotLoading, setForgotLoading] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
      setForgotOpen(false);
      toast.success("Si ce compte existe, un email de réinitialisation vient d'être envoyé.");
    } catch {
      toast.error("Une erreur est survenue, réessayez.");
    } finally {
      setForgotLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Connexion réussie");
      navigate("/");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de la connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 relative bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1546213290-e1b492ab3eee?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Store"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#0ea5e9] flex items-center justify-center">
              <Crown className="h-6 w-6" />
            </div>
            <span className="font-display font-bold text-xl">Vert Distribution</span>
          </div>
          <div>
            <h1 className="font-display text-4xl font-bold leading-tight mb-3">
              Pass Privilège<br />90 Jours
            </h1>
            <p className="text-slate-300 max-w-md">
              Gérez vos clients, commandes et adhésions. -15% automatique pour vos membres,
              relances WhatsApp par Donna.
            </p>
          </div>
          <p className="text-xs text-slate-400 tracking-wide">© 2026 Vert Distribution SARL</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 bg-[#f8fafc]">
        <div className="w-full max-w-sm animate-in-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-xl bg-[#0ea5e9] flex items-center justify-center">
              <Crown className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg">Vert Distribution</span>
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">
            Espace Administrateur
          </p>
          <h2 className="font-display text-3xl font-bold text-slate-900 mb-8">Connexion</h2>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-colors duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  data-testid="login-password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full px-4 py-2.5 pr-11 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-colors duration-200"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  aria-label={showPass ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPass ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-semibold transition-colors duration-200 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Se connecter
            </button>
          </form>

          <button
            type="button"
            data-testid="forgot-password-link"
            onClick={() => setForgotOpen(true)}
            className="mt-5 text-sm text-slate-500 hover:text-sky-600 font-medium transition-colors duration-200"
          >
            Mot de passe oublié ?
          </button>

          <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
            <DialogContent data-testid="forgot-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Mot de passe oublié</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitForgot}>
                <p className="text-sm text-slate-500 mb-4">
                  Saisissez l'adresse email de votre compte. Nous vous enverrons un lien pour définir un nouveau mot de passe.
                </p>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
                <DialogFooter className="mt-6">
                  <button type="button" onClick={() => setForgotOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium transition-colors duration-200">Annuler</button>
                  <button data-testid="forgot-submit" type="submit" disabled={forgotLoading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white text-sm font-semibold transition-colors duration-200 disabled:opacity-60">
                    {forgotLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Envoyer le lien
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
